<template>
  <div class="wrapper natural--seperate">
    <hr />
  </div>
  <div class="wrapper natural__intro">
    <h2 class="h2-custom" v-html="content.title"></h2>
    <div v-html="content.htmltext" class="natural__text"></div>
  </div>
  <div class="natural__images mobile">
    <DraggableImages
      v-for="(picture, index) in content.pictures"
      :key="index"
      active-after="l"
      selector="[data-list-image]"
    >
      <Picture
        :content="{ picture }"
        :style="`transform: ${
          index === 1 ? 'translateX(-50%) ' : ''
        } rotate(${deg(index % 2 !== 1)})`"
        :is-polaroid="true"
        sizes="1200px"
        data-list-image
      ></Picture>
    </DraggableImages>
  </div>
  <div class="natural__images desktop wrapper">
    <DraggableImages
      v-for="(picture, index) in content.pictures"
      :key="index"
      active-after="l"
    >
      <Picture
        :content="{ picture }"
        :style="`transform: ${
          index === 1 ? 'translateX(-50%) ' : ''
        } rotate(${deg(index % 2 !== 1)})`"
        :is-polaroid="true"
        data-draggable
      ></Picture>
    </DraggableImages>
  </div>
  <div class="wrapper">
    <div class="natural__list">
      <div
        v-for="(items, index) in content.items"
        :key="index"
        class="natural__items"
      >
        <h3 v-html="items.title"></h3>
        <p v-html="items.htmltext"></p>
      </div>
    </div>
  </div>
  <div class="wrapper natural--seperate">
    <hr />
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { PropType, onMounted } from 'vue'
import isMobile from 'ismobilejs'
// import { createSplitTitle, SplitTitle } from '@/animations/splitTitle'
import { Picture as PictureType } from '@/types'

import { useResize } from '@/utils/aware'
import Picture from '@/components/global/Picture.vue'
import DraggableImages from '@/components/global/DraggableImages.vue'

// import isMobile from 'ismobilejs'

interface Natural {
  title: string
  htmltext: string
  picture: PictureType[]
  items: {
    htmltext: string
    title: string
  }[]
  pictures: {
    pictures: PictureType[]
  }[]
}

defineProps({
  content: {
    type: Object as PropType<Natural>,
    required: true,
  },
})

const onWindowResize = () => {
  isMobile()
}

onMounted(() => {
  useResize(onWindowResize.bind(isMobile()))
})

const deg = (negative: boolean) => {
  const deg = negative
    ? gsap.utils.random(2, 20, 1)
    : gsap.utils.random(-2, -20, 1)

  return `${deg}deg`
}
</script>

<style lang="scss" scoped>
.wrapper {
  @include fluid(
    padding-top,
    (
      xxs: 20px,
      xxl: 80px,
    )
  );
}

.natural--seperate {
  @include fluid(
    padding-bottom,
    (
      xxs: 40px,
      xxl: 80px,
    )
  );
}

.natural__intro {
  :deep p {
    @include fluid(
      margin-bottom,
      (
        xxs: 20px,
        xxl: 28px,
      )
    );
    @include fluid(
      font-size,
      (
        xxs: 16px,
        xxl: 16px,
      )
    );
  }
}
.natural__text {
  margin-top: 40px;
  margin-left: auto;
  @include mq(m) {
    width: col(6);
    margin-top: -60px;
  }
}
.natural__list {
  @include mq(s) {
    gap: 60px;
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }

  .natural__items {
    @include mq($until: s) {
      margin-bottom: 40px;
    }
    @include mq(s) {
      grid-column: span 6 / span 6;
    }
    @include mq(nav) {
      grid-column: span 4 / span 4;
    }
    h3 {
      font-size: 20px;
      @include fluid(
        margin-bottom,
        (
          xxs: 20px,
          xxl: 30px,
        )
      );
    }
    p {
      font-size: 16px;
    }
  }
}

.natural__images {
  position: relative;
  @include mq(nav) {
    min-height: 734px;
    display: flex;
    align-items: center;
  }

  @include mq($until: nav) {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    margin: 60px auto;
    align-items: center;
    padding: 0 calc(9.6153846154vw - 10.7692307692px) 34px
      calc(9.6153846154vw - 10.7692307692px);

    @include scrollbars($foreground-color: var(--c-foreground));
  }

  .picture-outer {
    position: absolute;
    @include mq($until: nav) {
      min-width: 28rem;
      margin-right: 2rem;
      max-width: 46rem;
      width: col(10.5, 9);
      pointer-events: none;
      position: relative;
      transform: none !important;
    }

    @include mq(nav) {
      max-width: 400px;
      width: 400px;
      &:nth-child(1) {
        left: -200px;
      }
      &:nth-child(2) {
        left: 50%;
      }
      &:nth-child(3) {
        right: -200px;
      }
    }
    @include mq(xl) {
      max-width: 555px;
      width: 555px;
    }
  }
  &.mobile {
    display: flex;
    @include mq(nav) {
      display: none;
    }
  }
  &.desktop {
    display: none;
    @include mq(nav) {
      display: flex;
    }
  }
}
</style>
